import { useEffect, useState } from "react";
import { PointCloudGeneratorResult } from "@/types/laser-engraving";

/**
 * 点云就绪状态检测结果
 */
export interface PointCloudReadinessResult {
  /** 点云是否完全就绪（可安全启用阴影） */
  isPointCloudReady: boolean;
  /** 详细状态信息 */
  status: {
    hasGeometry: boolean;
    isGenerating: boolean;
    isFullyInitialized: boolean;
    isStable: boolean;
  };
}

/**
 * 点云就绪状态检测Hook
 *
 * 提供可靠的点云完全就绪信号，确保：
 * 1. 点云几何体存在且完整
 * 2. 生成过程已完成
 * 3. 几何体属性已同步到GPU
 * 4. 渲染已稳定（额外的稳定性检查）
 *
 * @param pointCloudResult - 点云生成器结果
 * @param displayMode - 当前显示模式
 * @param stabilityFrames - 稳定性检查所需帧数（默认3帧）
 * @returns 点云就绪状态
 */
export function usePointCloudReadiness(
  pointCloudResult: PointCloudGeneratorResult,
  displayMode: "pointCloud" | "model",
  stabilityFrames: number = 3,
): PointCloudReadinessResult {
  const [isStable, setIsStable] = useState(false);
  const [frameCount, setFrameCount] = useState(0);

  const { pointGeometry, isGenerating, isFullyInitialized } = pointCloudResult;

  // 基础状态检查
  const hasGeometry = pointGeometry !== null;
  const isPointCloudMode = displayMode === "pointCloud";

  // 🎯 核心就绪逻辑：点云模式下需要完全初始化，非点云模式立即就绪
  const basicReadiness = isPointCloudMode
    ? hasGeometry && !isGenerating && isFullyInitialized
    : true; // 非点云模式直接就绪

  // 🎯 稳定性检测：确保几何体在连续几帧中保持稳定
  useEffect(() => {
    if (!basicReadiness) {
      setIsStable(false);
      setFrameCount(0);
      return;
    }

    // 开始稳定性计数
    let currentFrame = 0;
    const checkStability = () => {
      currentFrame++;
      setFrameCount(currentFrame);

      if (currentFrame >= stabilityFrames) {
        console.log(`🎯 点云稳定性验证通过 (${currentFrame}帧)`);
        setIsStable(true);
      } else {
        // 继续下一帧检查
        requestAnimationFrame(checkStability);
      }
    };

    // 启动稳定性检查
    requestAnimationFrame(checkStability);

    // 清理函数
    return () => {
      setIsStable(false);
      setFrameCount(0);
    };
  }, [basicReadiness, stabilityFrames]);

  // 最终就绪状态
  const isPointCloudReady = basicReadiness && isStable;

  // 详细状态信息
  const status = {
    hasGeometry,
    isGenerating,
    isFullyInitialized,
    isStable,
  };

  // 调试日志
  useEffect(() => {
    console.log("🎯 点云就绪状态检测:", {
      displayMode,
      isPointCloudMode,
      basicReadiness,
      isStable,
      frameCount,
      isPointCloudReady,
      status,
    });
  }, [
    displayMode,
    isPointCloudMode,
    basicReadiness,
    isStable,
    frameCount,
    isPointCloudReady,
    status,
  ]);

  return {
    isPointCloudReady,
    status,
  };
}
